import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { constructBcsPublishedPayload } from '../utils/PayloadUtils';
import CloseButton from './ui/CloseButton';

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

// Configures + generates one full model test for a flat quiz level (BCS, Medical, …): total questions,
// duration (default `minutesPerQuestion × total`, editable), and an editable per-subject distribution
// (seeded from `distribution`, rescaled proportionally when the total changes). Created unpublished
// with no question reused across published model tests of that level.
//
// Props: distribution [{subject, count}], baseTotal, minutesPerQuestion, generateUrl, levelName, onClose, onGenerated.
const ModelTestBuilderModal = ({ distribution, baseTotal, minutesPerQuestion, generateUrl, levelName, onClose, onGenerated }) => {
    const CallApi = useApiCaller();

    // Largest-remainder (Hamilton) apportionment of `distribution` to `total`, so per-subject counts
    // stay proportional AND sum exactly to `total`.
    const rescaleDistribution = (total) => {
        const t = Math.max(0, Math.round(Number(total) || 0));
        const floors = distribution.map(d => {
            const exact = (d.count / baseTotal) * t;
            return { subject: d.subject, count: Math.floor(exact), rem: exact - Math.floor(exact) };
        });
        const remaining = t - floors.reduce((s, r) => s + r.count, 0);
        floors
            .map((r, i) => ({ i, rem: r.rem }))
            .sort((a, b) => b.rem - a.rem)
            .slice(0, Math.max(0, remaining))
            .forEach(({ i }) => { floors[i].count += 1; });
        const out = {};
        floors.forEach(r => { out[r.subject] = r.count; });
        return out;
    };

    const defaultCounts = () => {
        const out = {};
        distribution.forEach(d => { out[d.subject] = d.count; });
        return out;
    };

    const [total, setTotal] = useState(baseTotal);
    const [duration, setDuration] = useState(Math.round(minutesPerQuestion * baseTotal));
    const [durationTouched, setDurationTouched] = useState(false);
    const [counts, setCounts] = useState(defaultCounts);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // Total drives the distribution (proportional rescale) and, unless the admin edited it, the duration.
    const handleTotalChange = (value) => {
        setTotal(value);
        const t = Math.round(Number(value) || 0);
        setCounts(rescaleDistribution(t));
        if (!durationTouched) setDuration(Math.max(1, Math.round(minutesPerQuestion * t)));
    };

    const handleCountChange = (subject, value) => {
        const n = Math.max(0, Math.round(Number(value) || 0));
        setCounts(prev => ({ ...prev, [subject]: n }));
    };

    const sum = Object.values(counts).reduce((s, n) => s + (Number(n) || 0), 0);
    const canGenerate = sum >= 1 && Number(duration) >= 1 && !generating;

    const handleGenerate = async () => {
        setError('');
        setGenerating(true);
        try {
            const payload = constructBcsPublishedPayload(counts, duration); // { distribution, duration_minutes }
            const res = await CallApi({ url: generateUrl, method: 'POST', payload, token: true });
            if (res.success) {
                if (onGenerated) onGenerated(res.data);
            } else {
                setError(res.message || `Failed to generate ${levelName} model test.`);
            }
        } catch (err) {
            console.error(`Error generating ${levelName} model test:`, err);
            setError(`An error occurred while generating the ${levelName} model test.`);
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem',
        }}>
            <div style={{
                backgroundColor: '#ffffff', borderRadius: '0.9rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: 'min(640px, 96vw)',
                maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                <div style={{ background: PURPLE_GRADIENT, color: '#fff', padding: '1.1rem 1.25rem', position: 'relative' }}>
                    <CloseButton onClick={onClose} />
                    <h2 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText style={{ width: '1.25rem', height: '1.25rem' }} /> Generate Full Model Test — {levelName}
                    </h2>
                    <p style={{ margin: '0.35rem 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
                        Builds one unpublished {levelName} model test from the per-subject distribution below. No question
                        is reused across published {levelName} model tests.
                    </p>
                </div>

                <div style={{ padding: '1.25rem', overflowY: 'auto' }}>
                    {error && <p style={{ color: '#dc2626' }}>{error}</p>}

                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <label className="form-label fw-semibold">Total questions</label>
                            <input
                                type="number" min="1" className="form-control"
                                value={total}
                                onChange={e => handleTotalChange(e.target.value)}
                            />
                            <small className="form-text text-muted">Rescales the distribution below.</small>
                        </div>
                        <div className="col-6">
                            <label className="form-label fw-semibold">Duration (minutes)</label>
                            <input
                                type="number" min="1" className="form-control"
                                value={duration}
                                onChange={e => { setDuration(e.target.value); setDurationTouched(true); }}
                            />
                            <small className="form-text text-muted">Default {minutesPerQuestion} min/question.</small>
                        </div>
                    </div>

                    <label className="form-label fw-semibold">Per-subject distribution</label>
                    <div className="border rounded">
                        {distribution.map((d, i) => (
                            <div
                                key={d.subject}
                                className="d-flex align-items-center gap-2 px-2 py-1"
                                style={{ borderTop: i === 0 ? 'none' : '1px solid #eee' }}
                            >
                                <span className="flex-grow-1" style={{ fontSize: '0.9rem' }}>{d.subject}</span>
                                <input
                                    type="number" min="0" className="form-control form-control-sm"
                                    style={{ width: 90 }}
                                    value={counts[d.subject] ?? 0}
                                    onChange={e => handleCountChange(d.subject, e.target.value)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="d-flex justify-content-end mt-2">
                        <span className={`badge ${sum > 0 ? 'bg-primary' : 'bg-secondary'}`}>Sum: {sum}</span>
                    </div>
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
                    <button className="btn btn-light" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" disabled={!canGenerate} onClick={handleGenerate}>
                        {generating ? 'Generating…' : `Generate (${sum} questions)`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModelTestBuilderModal;
